import React from "react";

/**
 * A stepped slider over named choices — sensitivity, quality, anything with an ordered scale.
 *
 * Named stops rather than a raw number, because the thing being chosen is a trade-off ("fewer
 * false alarms" ↔ "earlier warning") and a number invites false precision about it. The ends are
 * labelled so the direction of the trade is legible without moving the handle.
 */
export function Slider<T extends string>({
    options,
    value,
    onChange,
    lowLabel,
    highLabel,
    disabled = false,
    className = "",
}: {
    options: { key: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
    /** What the left end means, in the reader's terms. */
    lowLabel?: string;
    highLabel?: string;
    disabled?: boolean;
    className?: string;
}): React.ReactElement {
    const index = Math.max(0, options.findIndex((o) => o.key === value));
    const last = Math.max(1, options.length - 1);

    return (
        <div className={`flex w-full min-w-[240px] max-w-[420px] flex-col gap-1.5 ${disabled ? "opacity-60" : ""} ${className}`}>
            <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-semibold text-[var(--accent)]">{options[index]?.label}</span>
                <span className="text-[11.5px] tabular-nums text-[var(--faint)]">
                    {index + 1} / {options.length}
                </span>
            </div>
            <input
                type="range"
                min={0}
                max={last}
                step={1}
                value={index}
                disabled={disabled}
                onChange={(e) => onChange(options[Number(e.target.value)].key)}
                aria-label={options[index]?.label}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[var(--track)] accent-[var(--accent)] disabled:cursor-default"
                style={{
                    background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${(index / last) * 100}%, var(--track) ${
                        (index / last) * 100
                    }%, var(--track) 100%)`,
                }}
            />
            {(lowLabel || highLabel) && (
                <div className="flex justify-between text-[11px] text-[var(--faint)]">
                    <span>{lowLabel}</span>
                    <span>{highLabel}</span>
                </div>
            )}
        </div>
    );
}
